/**
 * Admin V3 — Booking scheduling modal ("Planifier")
 *
 * Operator collects the operational ride logistics for a commercial shell
 * booking (status awaiting_schedule) and transitions it to `confirmed`
 * (dispatch-ready) via POST /admin/bookings/{id}/schedule. Pre-fills from the
 * booking/Devis lineage where available; required fields are validated before
 * submit. Does NOT assign a driver. Dark — only mounted when the action (gated
 * by VITE_COMMERCIAL_SCHEDULING) is invoked.
 */
import React, { useState, useEffect } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const REQUIRED = [
  'pickup_address', 'pickup_lat', 'pickup_lng',
  'destination_address', 'destination_lat', 'destination_lng',
  'scheduled_date', 'scheduled_time', 'passengers', 'vehicle_class',
];

const blank = {
  pickup_address: '', pickup_lat: '', pickup_lng: '',
  destination_address: '', destination_lat: '', destination_lng: '',
  scheduled_date: '', scheduled_time: '', passengers: '', vehicle_class: '',
};

const prefill = (b) => ({
  pickup_address: b.pickup_address ?? '',
  pickup_lat: b.pickup_lat ?? '', pickup_lng: b.pickup_lng ?? '',
  destination_address: b.destination_address ?? '',
  destination_lat: b.destination_lat ?? '', destination_lng: b.destination_lng ?? '',
  scheduled_date: b.date ?? '', scheduled_time: b.time ?? '',
  passengers: b.passengers ?? '', vehicle_class: b.vehicle_class ?? b.service_type ?? '',
});

const inputCls =
  'w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100';

// Module-level (stable identity) so controlled inputs keep focus across the
// parent's per-keystroke re-renders.
const Field = ({ k, label, value, missing, onChange, type = 'text', step }) => (
  <div>
    <label className="text-[12px] font-semibold text-slate-600">{label}</label>
    <input
      type={type} step={step} value={value} onChange={onChange}
      data-testid={`adminv3-schedule-${k}`}
      className={`${inputCls} ${missing ? 'border-rose-300' : ''}`}
    />
  </div>
);

const ScheduleBookingModal = ({ open, booking, onClose, onScheduled, locale = DEFAULT_LOCALE }) => {
  const id = booking?.id;
  const m = useAdminMutation(`/admin/bookings/${id || '_'}/schedule`, 'post');
  const { showSuccess, showError } = useToast();
  const [f, setF] = useState(blank);

  useEffect(() => {
    if (open && booking) setF(prefill(booking));
  }, [open, booking]);

  if (!open || !booking) return null;

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const isMissing = (k) => f[k] === '' || f[k] === null || f[k] === undefined;
  const missing = REQUIRED.filter(isMissing);
  const valid = missing.length === 0;

  const submit = () => {
    m.mutate(
      {
        pickup_address: f.pickup_address,
        pickup_lat: Number(f.pickup_lat), pickup_lng: Number(f.pickup_lng),
        destination_address: f.destination_address,
        destination_lat: Number(f.destination_lat), destination_lng: Number(f.destination_lng),
        scheduled_date: f.scheduled_date, scheduled_time: f.scheduled_time,
        passengers: Number(f.passengers), vehicle_class: f.vehicle_class,
      },
      {
        onSuccess: () => { showSuccess(t('schedule.ok', locale)); if (onScheduled) onScheduled(); },
        onError: (e) => showError(e?.response?.data?.detail || t('schedule.error', locale)),
      },
    );
  };

  const fld = (k, label, type, step) => (
    <Field k={k} label={label} value={f[k]} missing={isMissing(k)} onChange={set(k)} type={type} step={step} />
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="adminv3-schedule-modal">
      <div className="w-[560px] max-w-[94vw] rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-[17px] font-semibold text-slate-900">
            {t('schedule.title', locale)}{booking.booking_reference ? ` · ${booking.booking_reference}` : ''}
          </h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="adminv3-schedule-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {fld('pickup_address', t('schedule.field.pickup', locale))}
          {fld('destination_address', t('schedule.field.destination', locale))}
          {fld('pickup_lat', t('schedule.field.pickupLat', locale), 'number', 'any')}
          {fld('pickup_lng', t('schedule.field.pickupLng', locale), 'number', 'any')}
          {fld('destination_lat', t('schedule.field.destLat', locale), 'number', 'any')}
          {fld('destination_lng', t('schedule.field.destLng', locale), 'number', 'any')}
          {fld('scheduled_date', t('schedule.field.date', locale), 'date')}
          {fld('scheduled_time', t('schedule.field.time', locale), 'time')}
          {fld('passengers', t('schedule.field.passengers', locale), 'number')}
          {fld('vehicle_class', t('schedule.field.vehicleClass', locale))}
        </div>

        {!valid && (
          <p className="mt-3 text-[12px] text-rose-600" data-testid="adminv3-schedule-missing">
            {t('schedule.missingHint', locale)}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="adminv3-schedule-cancel">
            {t('schedule.cancel', locale)}
          </Button>
          <Button variant="default" size="sm" onClick={submit} disabled={!valid || m.loading} data-testid="adminv3-schedule-submit">
            {m.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {t('schedule.submit', locale)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleBookingModal;
